import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import ActiveUser from '../../../home/cards/ActiveUser';
import ProgramRoi from '../../../home/cards/ProgramRoi';
import GoalProgress from '../../../home/cards/GoalProgress';
import { useModule } from '../../../../../../utils/useModule';
import { useAppSelector } from '../../../../../../redux/hooks';
import MySkeleton from '../../../../../../components/Skeleton';
import ExperiencePoints from '../../../home/cards/ExperiencePoints';
import useGroupOverview from '../../../../../../queries/useGroupOverview';

interface EngagementMetricsProps {
  showActiveUser?: boolean;
  showProgramRoi?: boolean;
  showGoalProgress?: boolean;
  showExperiencePoints?: boolean;
  grpIdOverride?: string;
}

const EngagementMetrics: React.FC<EngagementMetricsProps> = ({
  showActiveUser = true,
  showProgramRoi = true,
  showGoalProgress,
  showExperiencePoints = true,
  grpIdOverride
}) => {
  const { groupId: rawGrpId, orgId } = useProgramContext();
  let routeGrpId = rawGrpId;
  if (routeGrpId === 'admin') {
    routeGrpId = '';
  }
  const effectiveGrpId = grpIdOverride ?? routeGrpId;
  const { isMentorship } = useModule();
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [goalProgressValue, setGoalProgressValue] = useState(0);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [programRoiLoading, setProgramRoiLoading] = useState(false);
  const [programRoiValue, setRoiValue] = useState(0);
  const [xpPointsValue, setXpPoints] = useState(0);
  const { isLoading, isSuccess, isError, data } = useGroupOverview({ orgId, grpId: effectiveGrpId });
  const orgCurrency = useAppSelector((state) => state.orgDetails.data?.currency);
  const [hasShowGoalProgressValue, setHasShowGoalProgressValue] = useState<any>();

  // Get Currency Symbol
  useEffect(() => {
    setCurrencySymbol(orgCurrency);
  }, [orgCurrency]);

  // Get data
  useEffect(() => {
    if (data) {
      setRoiValue(data.programROI || 0);
      setGoalProgressValue(data.goalProgress || 0);
      setActiveUserCount(data.activeUsers || 0);
      setXpPoints(data.totalXpPoint || 0);
      setHasShowGoalProgressValue(data.showGoalProgress);
    }
  }, [isSuccess, effectiveGrpId, orgId, data]);

  /** Count the number of true values */
  const trueValues = [
    showActiveUser,
    showProgramRoi,
    hasShowGoalProgressValue && !isMentorship,
    showExperiencePoints
  ].filter(Boolean).length;

  /** Skeleton loader default we made 4 cards */
  const skeletonLoader = () =>
    Array.from({ length: 4 }).map((_, index) => (
      <React.Fragment key={index}>
        <Grid item xs={12} md={6} lg={12 / 4}>
          <MySkeleton variant="rounded" height={245} />
        </Grid>
      </React.Fragment>
    ));

  return (
    <>
      {isLoading ? (
        skeletonLoader()
      ) : (
        <>
          {/* Active users */}
          {showActiveUser && (
            <Grid item xs={12} md={6} lg={12 / trueValues}>
              <ActiveUser activeUserCount={activeUserCount} />
            </Grid>
          )}

          {/* Program ROI */}
          {showProgramRoi && (
            <Grid item xs={12} md={6} lg={12 / trueValues}>
              <ProgramRoi
                programRoi={programRoiValue}
                programRoiLoading={programRoiLoading}
                currencySymbol={currencySymbol}
              />
            </Grid>
          )}

          {/* Goal progress chart */}
          {hasShowGoalProgressValue && !isMentorship && (
            <Grid item xs={12} md={6} lg={12 / trueValues}>
              <GoalProgress goalProgressValue={goalProgressValue} />
            </Grid>
          )}

          {/* Experience Points */}
          {showExperiencePoints && (
            <Grid item xs={12} md={6} lg={12 / trueValues}>
              <ExperiencePoints value={xpPointsValue} />
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default EngagementMetrics;
